import {
  BacktestingFilterStatus,
  EntryZoneCategory,
  FiltersActions,
  FilterSectionType,
  FiltersRequest,
  FilterType,
  FixMeLater,
  LanguageNameSpaces,
  RawFiltersData,
  Status,
} from "@shared/types";
import { HOT_TRADES_HELPER_DESCRIPTION } from "@shared/common/constants";
import { TranslationKeys } from "@shared/common/i18n/types";
import { FollowSignalStatus } from "@shared/features/signals/common/types";

export const FILTER_TYPES_DATA: Record<
  FilterType,
  {
    queryKey: string;
    title: TranslationKeys<LanguageNameSpaces.COMMON>;
    setActionName?: keyof FiltersActions;
    filterSectionType: FilterSectionType;
    constantsKey?: keyof RawFiltersData["constants"];
    showSingleOption?: boolean;
    showEmptyOptions?: boolean;
    defaultValue?: FixMeLater;
  }
> = {
  [FilterType.EXCHANGES]: {
    queryKey: "exchanges",
    title: "filters.titles.exchanges",
    setActionName: "setExchangesFilter",
    filterSectionType: FilterSectionType.CHECKBOX,
    constantsKey: "Exchanges",
    showSingleOption: true,
  },
  [FilterType.SYMBOLS]: {
    queryKey: "symbols",
    title: "filters.titles.symbols",
    setActionName: "setSymbolsFilter",
    filterSectionType: FilterSectionType.MULTI_SELECT,
  },
  [FilterType.CLIENTS]: {
    queryKey: "accounts",
    title: "filters.titles.accounts",
    setActionName: "setClientsFilter",
    filterSectionType: FilterSectionType.CHECKBOX,
    constantsKey: "Accounts",
    showSingleOption: true,
  },
  [FilterType.STATUS]: {
    queryKey: "status",
    title: "filters.titles.status",
    setActionName: "setStatusesFilter",
    filterSectionType: FilterSectionType.CHECKBOX,
  },
  [FilterType.LONG_SHORT]: {
    queryKey: "direction",
    title: "filters.titles.direction",
    setActionName: "setLongShortFilter",
    filterSectionType: FilterSectionType.CHECKBOX,
    constantsKey: "Long/Short",
  },
  [FilterType.SOURCE_NAME]: {
    queryKey: "source-name",
    title: "filters.titles.source_name",
    setActionName: "setSourceFilter",
    filterSectionType: FilterSectionType.CHECKBOX,
    constantsKey: "Source New",
  },
  [FilterType.SIGNAL_STATUS]: {
    queryKey: "signal-status",
    title: "filters.titles.signal_status",
    setActionName: "setSignalStatusFilter",
    filterSectionType: FilterSectionType.CHECKBOX,
  },
  [FilterType.FOLLOW_STATUS]: {
    queryKey: "follow-status",
    title: "filters.titles.follow_status",
    setActionName: "setFollowStatusFilter",
    filterSectionType: FilterSectionType.CHECKBOX,
  },
  [FilterType.SOURCE_TYPE]: {
    queryKey: "source-type",
    title: "filters.titles.source_type",
    setActionName: "setSourceTypeFilter",
    filterSectionType: FilterSectionType.CHECKBOX,
    constantsKey: "Source Type",
  },
  [FilterType.MARGIN_TYPE]: {
    queryKey: "margin-type",
    title: "filters.titles.margin_type",
    setActionName: "setMarginTypeFilter",
    filterSectionType: FilterSectionType.CHECKBOX,
    constantsKey: "Margin Type",
  },
  [FilterType.TRADE_TYPE]: {
    queryKey: "trade-type",
    title: "filters.titles.trade_type",
    setActionName: "setTradeTypeFilter",
    filterSectionType: FilterSectionType.CHECKBOX,
    constantsKey: "Trade Type",
  },
  [FilterType.LEVERAGE]: {
    queryKey: "leverage",
    title: "filters.titles.leverage",
    setActionName: "setLeverageFilter",
    filterSectionType: FilterSectionType.SLIDER,
  },
  [FilterType.CONSECUTIVE_ACTIVE_DAYS]: {
    queryKey: "active-days",
    title: "filters.titles.days_running",
    setActionName: "setConsecutiveActiveDaysFilter",
    filterSectionType: FilterSectionType.SLIDER,
  },
  [FilterType.TIME_FRAME]: {
    queryKey: "time-frame",
    title: "filters.titles.time_range",
    setActionName: "setTimeFrameFilter",
    filterSectionType: FilterSectionType.TIME_RANGE,
    showEmptyOptions: true,
  },
  [FilterType.GROUPS]: {
    queryKey: "groups",
    title: "filters.titles.groups",
    setActionName: "setGroupsFilter",
    filterSectionType: FilterSectionType.RADIO,
  },
  [FilterType.MULTI_GROUPS]: {
    queryKey: "multi-groups",
    title: "filters.titles.groups",
    setActionName: "setMultiGroupsFilter",
    filterSectionType: FilterSectionType.CHECKBOX,
  },
  [FilterType.AMOUNT_TYPE]: {
    queryKey: "amount-type",
    title: "filters.titles.amount_type",
    setActionName: "setAmountType",
    filterSectionType: FilterSectionType.RADIO,
  },
  [FilterType.PRICE_RANGE]: {
    queryKey: "price-range",
    title: "filters.titles.price_range",
    setActionName: "setPriceFilter",
    filterSectionType: FilterSectionType.PRICE_RANGE,
  },
  [FilterType.USD_PRICE_RANGE]: {
    queryKey: "usd-price",
    title: "filters.titles.monthly_usd_price",
    setActionName: "setUsdPriceRangeFilter",
    filterSectionType: FilterSectionType.SLIDER,
  },
  [FilterType.AVG_NUM_SIGNALS]: {
    queryKey: "avg-signals",
    title: "filters.titles.avg_signals",
    setActionName: "setAvgNumSignalsFilter",
    filterSectionType: FilterSectionType.SLIDER,
  },
  [FilterType.AVG_RATING]: {
    queryKey: "avg-rating",
    title: "filters.titles.avg_rating",
    setActionName: "setAvgRatingFilter",
    filterSectionType: FilterSectionType.CHECKBOX,
  },
  [FilterType.IS_HOT]: {
    queryKey: "is-hot",
    title: "filters.titles.hot_trades",
    setActionName: "setIsHotFilter",
    filterSectionType: FilterSectionType.BOOLEAN,
    defaultValue: false,
  },
  [FilterType.VALID_BOT_AMOUNT]: {
    queryKey: "hide-insufficient-funds",
    title: "filters.titles.bot_amount",
    setActionName: "setValidBotAmountFilter",
    filterSectionType: FilterSectionType.BOOLEAN,
    defaultValue: true,
  },
  [FilterType.TAGS]: {
    queryKey: "tags",
    title: "filters.titles.tags",
    setActionName: "setTagsFilter",
    filterSectionType: FilterSectionType.TAGS,
    defaultValue: [],
  },
  [FilterType.BACKTESTING_STATUS]: {
    queryKey: "backtesting-status",
    title: "filters.titles.status",
    setActionName: "setBacktestingStatusFilter",
    filterSectionType: FilterSectionType.CHECKBOX,
  },
};

export const SINGLE_FILTERS_ROW_BREAKPOINT = "800px";

export const FILTERS_HELPER_TOOLTIP_DATA: {
  [key in FilterType]?: any; // TODO: fix this to point to helper tooltip props
} = {
  [FilterType.VALID_BOT_AMOUNT]: {
    description: "tooltips.valid_bot_amount",
    analyticsFieldName: "Valid Bot Amount",
    title: "Bot Amount",
  },
  [FilterType.IS_HOT]: {
    description: HOT_TRADES_HELPER_DESCRIPTION,
    analyticsFieldName: "Is Hot",
    title: "Hot Trades",
  },
};

export const SIGNAL_STATUS_TO_DISPLAY: Record<
  EntryZoneCategory,
  TranslationKeys<LanguageNameSpaces.COMMON>
> = {
  [EntryZoneCategory.ABOVE_ENTRY_ZONE]: "filters.status.signals.above_entries",
  [EntryZoneCategory.TOP_ENTRY_ZONE]: "filters.status.signals.top_entry_zone",
  [EntryZoneCategory.MID_ENTRY_ZONE]:
    "filters.status.signals.middle_entry_zone",
  [EntryZoneCategory.BOTTOM_ENTRY_ZONE]:
    "filters.status.signals.bottom_entry_zone",
  [EntryZoneCategory.BELOW_ENTRY_ZONE]: "filters.status.signals.below_entries",
};

export const FILTER_TYPE_TO_REQUEST_KEY: {
  [key in FilterType]?: keyof FiltersRequest;
} = {
  [FilterType.MARGIN_TYPE]: "margin_type",
  [FilterType.CLIENTS]: "client_ids",
  [FilterType.TRADE_TYPE]: "trade_types",
  [FilterType.LONG_SHORT]: "reverse",
  [FilterType.TIME_FRAME]: "close_timestamp",
  [FilterType.LEVERAGE]: "multiplier",
  [FilterType.SOURCE_NAME]: "source",
} as const;

export const OPEN_TRADES_FILTER_TYPE_TO_REQUEST_KEY: {
  [key in FilterType]?: keyof FiltersRequest;
} = {
  ...FILTER_TYPE_TO_REQUEST_KEY,
  [FilterType.TIME_FRAME]: "open_timestamp",
} as const;

export const DELETED_ITEM_REGEX = /\(Deleted - \d+\)/;

export const FOLLOW_STATUS_TO_DISPLAY: Record<
  FollowSignalStatus,
  TranslationKeys<LanguageNameSpaces.COMMON>
> = {
  [FollowSignalStatus.FOLLOWING]: "filters.status.follow.following",
  [FollowSignalStatus.NOT_FOLLOWING]: "filters.status.follow.not_following",
};

export const STATUS_TO_DISPLAY: Record<
  Status,
  TranslationKeys<LanguageNameSpaces.COMMON>
> = {
  [Status.ACTIVE]: "filters.status.common.active",
  [Status.INACTIVE]: "filters.status.common.inactive",
  [Status.INVALID]: "filters.status.common.invalid",
  [Status.ARCHIVED]: "filters.status.common.archived",
};

export const BACKTESTING_STATUS_TO_DISPLAY: Record<
  BacktestingFilterStatus,
  TranslationKeys<LanguageNameSpaces.COMMON>
> = {
  [BacktestingFilterStatus.IN_PROGRESS]:
    "filters.status.backtesting.in_progress",
  [BacktestingFilterStatus.COMPLETED]: "filters.status.backtesting.completed",
  [BacktestingFilterStatus.FAILED]: "filters.status.backtesting.failed",
};
